// ============== PER-USER BLUESKY CONNECTION ==============
// No OAuth review needed — users paste their handle + an app password
// (bsky.app → Settings → Privacy & Security → App Passwords).
import {
  useState, useEffect, axios, Button, Card, CardContent, CardHeader, CardTitle,
  Badge, Loader2, Input, API, toast,
} from "@/pages/shared";

export const BlueskyPersonalSection = () => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [handle, setHandle] = useState("");
  const [appPassword, setAppPassword] = useState("");

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/social-connections/bluesky/status`);
      setStatus(r.data);
    } catch (e) { setStatus({ connected: false }); }
    setLoading(false);
  };

  useEffect(() => { fetchStatus(); }, []);

  const handleConnect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/social-connections/bluesky/connect`, { handle, app_password: appPassword });
      toast.success("Bluesky connected — auto-posts will cross-post to your feed");
      setShowForm(false); setHandle(""); setAppPassword("");
      await fetchStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't connect Bluesky");
    }
    setBusy(false);
  };

  const handleDisconnect = async () => {
    if (!confirm("Disconnect Bluesky?")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/social-connections/bluesky/disconnect`);
      toast.success("Bluesky disconnected");
      await fetchStatus();
    } catch (e) { toast.error("Couldn't disconnect"); }
    setBusy(false);
  };

  return (
    <Card className="glass" data-testid="settings-bluesky-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <svg className="w-5 h-5 text-sky-400" fill="currentColor" viewBox="0 0 24 24"><path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.624 6.479.815 2.736 3.713 3.66 6.383 3.364.136-.02.275-.039.415-.056-.138.022-.276.04-.415.056-3.912.58-7.387 2.005-2.83 7.078 5.013 5.19 6.87-1.113 7.823-4.308.953 3.195 2.05 9.271 7.733 4.308 4.267-4.308 1.172-6.498-2.74-7.078a8.741 8.741 0 0 1-.415-.056c.14.017.279.036.415.056 2.67.297 5.568-.628 6.383-3.364.246-.828.624-5.79.624-6.478 0-.69-.139-1.861-.902-2.206-.659-.298-1.664-.62-4.3 1.24C16.046 4.748 13.087 8.687 12 10.8z"/></svg>
          Your Bluesky account
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Connect Bluesky and every scheduled auto-post also publishes to your Bluesky feed.
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <Loader2 className="w-5 h-5 animate-spin" />
        ) : status?.connected ? (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium" data-testid="bluesky-handle">@{status.handle}</p>
              <p className="text-xs text-muted-foreground">Cross-posting on every scheduled auto-post</p>
            </div>
            <div className="flex items-center gap-2">
              <Badge className="bg-teal-500/20 text-teal-400" data-testid="bluesky-badge">✓ Connected</Badge>
              <Button variant="outline" size="sm" onClick={handleDisconnect} disabled={busy} data-testid="bluesky-disconnect-btn">Disconnect</Button>
            </div>
          </div>
        ) : showForm ? (
          <form onSubmit={handleConnect} className="space-y-3" data-testid="bluesky-connect-form">
            <Input value={handle} onChange={(e) => setHandle(e.target.value)} placeholder="yourname.bsky.social" required data-testid="bluesky-handle-input" />
            <Input value={appPassword} onChange={(e) => setAppPassword(e.target.value)} type="password" placeholder="App password (xxxx-xxxx-xxxx-xxxx)" required data-testid="bluesky-password-input" />
            <p className="text-xs text-muted-foreground">
              Create an app password at <a href="https://bsky.app/settings/app-passwords" target="_blank" rel="noreferrer" className="text-violet-400 underline">bsky.app → Settings → App Passwords</a> — never use your main password.
            </p>
            <div className="flex gap-2">
              <Button type="submit" disabled={busy} className="bg-violet-600 hover:bg-violet-700" data-testid="bluesky-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Connect Bluesky"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        ) : (
          <div className="flex items-center justify-between gap-4 p-3 rounded-lg bg-white/5">
            <div>
              <p className="font-medium">Bluesky not connected</p>
              <p className="text-xs text-muted-foreground">Takes 30 seconds with an app password — no approval process.</p>
            </div>
            <Button onClick={() => setShowForm(true)} data-testid="bluesky-connect-btn">Connect Bluesky</Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
